// Phase 5 (web revival) — Stream C.
//
// Right-rail metadata for a single asset:
//   - inline-editable caption (debounced 500ms after typing stops)
//   - favorite + archived toggles (PATCH on click, optimistic)
//   - capture time (local), GPS link, pixel dims, EXIF block
//   - delete + restore footer actions
//
// All write paths go through PATCH /v1/assets/{id} (caption / favorite /
// archived) or DELETE / POST .../restore for the trash flow. WS handles
// the cache invalidation; we just await the mutation.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api, ApiError } from '../../api/client';
import { qk } from '../../app/queryClient';
import {
  downloadAsset,
  extForUTI,
  formatBytes,
  pickDownloadResource,
} from '../../lib/download';
import type {
  AssetDetailDTO,
  PatchAssetRequest,
} from '../../api/types';

interface MetadataPanelProps {
  asset: AssetDetailDTO;
  isOwner: boolean;
}

export function AssetMetadataPanel({ asset, isOwner }: MetadataPanelProps) {
  return (
    <div className="flex flex-col gap-5 px-5 py-4 text-sm text-neutral-200">
      <CaptionEditor asset={asset} editable={isOwner} />
      <FlagsRow asset={asset} editable={isOwner} />
      <DownloadButton asset={asset} />
      <FactsBlock asset={asset} />
      <ExifBlock asset={asset} />
      {isOwner && <DangerZone asset={asset} />}
    </div>
  );
}

// ────────────────────── Caption ──────────────────────

function CaptionEditor({
  asset,
  editable,
}: {
  asset: AssetDetailDTO;
  editable: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(asset.caption ?? '');
  const lastSaved = useRef(asset.caption ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync external updates (WS push, refetch) into the draft when we're
  // not actively editing.
  useEffect(() => {
    if (!editing) {
      setDraft(asset.caption ?? '');
      lastSaved.current = asset.caption ?? '';
    }
  }, [asset.caption, editing]);

  const mutation = useMutation<AssetDetailDTO, ApiError, PatchAssetRequest>({
    mutationFn: (body) =>
      api.patch<AssetDetailDTO>(`/v1/assets/${asset.id}`, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(qk.asset(asset.id), updated);
      lastSaved.current = updated.caption ?? '';
    },
  });

  function scheduleSave(value: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value === lastSaved.current) return;
      mutation.mutate({ caption: value.length === 0 ? null : value });
    }, 500);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  if (!editable) {
    return (
      <div>
        <div className="mb-1 text-[11px] uppercase tracking-[0.14em] text-neutral-500">
          Caption
        </div>
        <div className="text-neutral-100">
          {asset.caption ?? <span className="text-neutral-500">No caption.</span>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">
          Caption
        </div>
        {mutation.isPending && (
          <span className="text-[10px] text-neutral-500">saving…</span>
        )}
        {mutation.isError && (
          <span className="text-[10px] text-red-400">{mutation.error.reason}</span>
        )}
      </div>
      {editing ? (
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            scheduleSave(e.target.value);
          }}
          onBlur={() => {
            // Flush any pending debounce so the trip out of edit mode
            // doesn't drop the last keystrokes.
            if (debounceRef.current) clearTimeout(debounceRef.current);
            if (draft !== lastSaved.current) {
              mutation.mutate({
                caption: draft.length === 0 ? null : draft,
              });
            }
            setEditing(false);
          }}
          rows={3}
          placeholder="Add a caption…"
          className="w-full resize-y rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5 text-sm text-neutral-100 placeholder:text-neutral-500 focus-visible:border-neutral-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-500"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="block w-full rounded-md px-2 py-1.5 text-left text-neutral-100 hover:bg-neutral-800"
        >
          {asset.caption ?? (
            <span className="text-neutral-500">Add a caption…</span>
          )}
        </button>
      )}
    </div>
  );
}

// ────────────────────── Flags (favorite / archive) ──────────────────────

function FlagsRow({
  asset,
  editable,
}: {
  asset: AssetDetailDTO;
  editable: boolean;
}) {
  const queryClient = useQueryClient();

  const mutation = useMutation<AssetDetailDTO, ApiError, PatchAssetRequest>({
    mutationFn: (body) =>
      api.patch<AssetDetailDTO>(`/v1/assets/${asset.id}`, body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: qk.asset(asset.id) });
      const prev = queryClient.getQueryData<AssetDetailDTO>(qk.asset(asset.id));
      if (prev) {
        queryClient.setQueryData<AssetDetailDTO>(qk.asset(asset.id), {
          ...prev,
          ...(body.favorite !== undefined ? { favorite: body.favorite } : {}),
          ...(body.archived !== undefined ? { archived: body.archived } : {}),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      const c = ctx as { prev?: AssetDetailDTO } | undefined;
      if (c?.prev) queryClient.setQueryData(qk.asset(asset.id), c.prev);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(qk.asset(asset.id), updated);
    },
  });

  const pillBase =
    'flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors disabled:opacity-50';
  const favClass = asset.favorite
    ? 'border-pink-500/60 bg-pink-950/40 text-pink-200'
    : 'border-neutral-700 bg-neutral-900 text-neutral-300 hover:bg-neutral-800';
  const archClass = asset.archived
    ? 'border-amber-500/60 bg-amber-950/40 text-amber-200'
    : 'border-neutral-700 bg-neutral-900 text-neutral-300 hover:bg-neutral-800';

  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        disabled={!editable || mutation.isPending}
        onClick={() => mutation.mutate({ favorite: !asset.favorite })}
        className={`${pillBase} ${favClass}`}
        aria-pressed={asset.favorite}
      >
        <span aria-hidden>{asset.favorite ? '❤' : '♡'}</span>
        Favorite
      </button>
      <button
        type="button"
        disabled={!editable || mutation.isPending}
        onClick={() => mutation.mutate({ archived: !asset.archived })}
        className={`${pillBase} ${archClass}`}
        aria-pressed={asset.archived}
      >
        <span aria-hidden>{asset.archived ? '▣' : '▢'}</span>
        Archived
      </button>
    </div>
  );
}

// ────────────────────── Download ──────────────────────

function DownloadButton({ asset }: { asset: AssetDetailDTO }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const resource = pickDownloadResource(asset);
  if (!resource) return null;

  const noun = asset.kind === 'video' ? 'video' : 'photo';
  const ext = extForUTI(resource.uti).toUpperCase();
  const size = formatBytes(resource.size_bytes);

  async function onClick() {
    setErr(null);
    setBusy(true);
    try {
      await downloadAsset(asset);
    } catch (e) {
      setErr(e instanceof ApiError ? e.reason : 'Download failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        className="flex w-full items-center justify-center gap-2 rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm font-medium text-neutral-100 transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        {busy ? 'Preparing…' : `Download ${noun}`}
      </button>
      <p className="mt-1 text-[11px] text-neutral-500">
        Original · {ext}
        {size ? ` · ${size}` : ''}
      </p>
      {err && <p className="mt-1 text-[11px] text-red-400">{err}</p>}
    </div>
  );
}

// ────────────────────── Facts (capture, gps, dims) ──────────────────────

function FactsBlock({ asset }: { asset: AssetDetailDTO }) {
  return (
    <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-xs">
      <Row label="Captured" value={formatCaptureTime(asset.capture_time_utc, asset.capture_tz_offset_seconds)} />
      <Row
        label="Dimensions"
        value={
          asset.pixel_w && asset.pixel_h
            ? `${asset.pixel_w} × ${asset.pixel_h}`
            : '—'
        }
      />
      <Row label="Kind" value={asset.kind} />
      <Row
        label="Location"
        value={
          // Vapor's default JSON encoder omits nil Swift optionals (no
          // `null` in the wire bytes), so these fields are `undefined`
          // at runtime when missing — not `null`. `!= null` catches both;
          // `!== null` would let undefined through and crash .toFixed.
          asset.gps_lat != null && asset.gps_lon != null ? (
            <a
              href={`https://www.openstreetmap.org/?mlat=${asset.gps_lat}&mlon=${asset.gps_lon}#map=15/${asset.gps_lat}/${asset.gps_lon}`}
              target="_blank"
              rel="noreferrer noopener"
              className="text-sky-300 hover:underline"
            >
              {asset.gps_lat.toFixed(4)}, {asset.gps_lon.toFixed(4)}
            </a>
          ) : (
            '—'
          )
        }
      />
      {asset.has_edits && <Row label="Edits" value="Has edits" />}
      {asset.deleted_at && (
        <Row
          label="In trash"
          value={<span className="text-amber-300">{formatLocal(asset.deleted_at)}</span>}
        />
      )}
    </dl>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-neutral-500">{label}</dt>
      <dd className="text-neutral-200">{value}</dd>
    </>
  );
}

// ────────────────────── EXIF ──────────────────────

function ExifBlock({ asset }: { asset: AssetDetailDTO }) {
  const e = asset.exif;
  const rows: { label: string; value: string }[] = [];
  if (e.camera_make || e.camera_model) {
    rows.push({
      label: 'Camera',
      value: [e.camera_make, e.camera_model].filter(Boolean).join(' '),
    });
  }
  if (e.lens_model) rows.push({ label: 'Lens', value: e.lens_model });
  // `!= null` covers both null (explicit) and undefined (omitted by the
  // Swift JSON encoder). Otherwise we'd push "undefined"/"f/undefined"
  // string interpolations or crash inside formatShutter.
  if (e.iso != null) rows.push({ label: 'ISO', value: `${e.iso}` });
  if (e.aperture != null)
    rows.push({ label: 'Aperture', value: `f/${e.aperture}` });
  if (e.shutter_seconds != null)
    rows.push({ label: 'Shutter', value: formatShutter(e.shutter_seconds) });
  if (e.focal_length_mm != null)
    rows.push({ label: 'Focal length', value: `${e.focal_length_mm}mm` });
  if (e.color_profile)
    rows.push({ label: 'Color', value: e.color_profile });

  if (rows.length === 0) return null;
  return (
    <div>
      <div className="mb-1 text-[11px] uppercase tracking-[0.14em] text-neutral-500">
        EXIF
      </div>
      <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-xs">
        {rows.map((r) => (
          <Row key={r.label} label={r.label} value={r.value} />
        ))}
      </dl>
    </div>
  );
}

// ────────────────────── Danger zone (delete + restore) ──────────────────────

function DangerZone({ asset }: { asset: AssetDetailDTO }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation<void, ApiError, void>({
    mutationFn: () => api.delete<void>(`/v1/assets/${asset.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.assets() });
      navigate({ to: '/library' });
    },
  });

  const restoreMutation = useMutation<void, ApiError, void>({
    mutationFn: () => api.post<void>(`/v1/assets/${asset.id}/restore`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.asset(asset.id) });
      queryClient.invalidateQueries({ queryKey: qk.assets() });
    },
  });

  const isTrashed = asset.deleted_at != null;

  return (
    <div className="mt-2 border-t border-white/5 pt-4">
      {!isTrashed ? (
        <button
          type="button"
          disabled={deleteMutation.isPending}
          onClick={() => {
            if (!window.confirm('Move this photo to trash?')) return;
            deleteMutation.mutate();
          }}
          className="w-full rounded-md border border-red-900/70 bg-red-950/40 px-3 py-2 text-xs font-medium text-red-200 hover:bg-red-900/40 disabled:opacity-50"
        >
          {deleteMutation.isPending ? 'Deleting…' : 'Move to trash'}
        </button>
      ) : (
        <button
          type="button"
          disabled={restoreMutation.isPending}
          onClick={() => restoreMutation.mutate()}
          className="w-full rounded-md border border-emerald-900/70 bg-emerald-950/40 px-3 py-2 text-xs font-medium text-emerald-200 hover:bg-emerald-900/40 disabled:opacity-50"
        >
          {restoreMutation.isPending ? 'Restoring…' : 'Restore from trash'}
        </button>
      )}
      {(deleteMutation.error || restoreMutation.error) && (
        <p className="mt-2 text-xs text-red-300">
          {(deleteMutation.error ?? restoreMutation.error)?.reason}
        </p>
      )}
    </div>
  );
}

// ────────────────────── helpers ──────────────────────

function formatCaptureTime(
  iso: string | null | undefined,
  tzOffsetSeconds: number | null | undefined,
): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    // Always render the CAPTURE-LOCATION wall clock, never the viewer's local
    // time. We do that by shifting the instant by the capture offset and then
    // formatting in UTC (below), so this must be pinned to UTC.
    timeZone: 'UTC',
  };
  if (tzOffsetSeconds == null) {
    // No offset known. The stored instant is the wall clock (photos without
    // OffsetTimeOriginal were parsed as wall-clock-as-UTC); render it as UTC.
    return d.toLocaleString(undefined, opts);
  }
  // Shift by the offset so the UTC-formatted result is the capture-location
  // wall clock, then annotate with the zone so it's unambiguous.
  const shifted = new Date(d.getTime() + tzOffsetSeconds * 1000);
  const wall = shifted.toLocaleString(undefined, opts);
  const sign = tzOffsetSeconds >= 0 ? '+' : '-';
  const abs = Math.abs(tzOffsetSeconds);
  const hh = String(Math.floor(abs / 3600)).padStart(2, '0');
  const mm = String(Math.floor((abs % 3600) / 60)).padStart(2, '0');
  return `${wall} (UTC${sign}${hh}:${mm})`;
}

function formatLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function formatShutter(s: number): string {
  if (s >= 1) return `${s}s`;
  // 1/60s style. Round to nearest standard speed denominator.
  const denom = Math.round(1 / s);
  return `1/${denom}s`;
}
